import PropTypes from 'prop-types'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button, isDisabled } from './Button'

export function InlineConfirm({ children, onConfirm, ...props }) {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(false)
  const root = useRef(null)
  const trigger = useRef(null)
  const cancel = useRef(null)
  const group = useRef(null)
  const wasOpen = useRef(false)
  const refocus = useRef(true)
  const containers = useRef([])
  const endWatch = useRef(() => {})
  const blocked = isDisabled(props)
  const close = (returnFocus = true) => {
    refocus.current = returnFocus
    setOpen(false)
  }
  useLayoutEffect(() => window.MEButtons.fitInlineQuestion(root.current), [])
  useLayoutEffect(() => {
    if (open) {
      containers.current = window.MEButtons.containersOf(root.current)
      cancel.current.focus()
    } else if (wasOpen.current) {
      // Any watch from a confirmed request ends once the confirm closes with
      // its trigger still mounted.
      endWatch.current()
      const { keepsFocus } = window.MEButtons
      if (refocus.current && keepsFocus(root.current)) trigger.current?.focus()
    }
    wasOpen.current = open
  }, [open])
  useEffect(() => {
    if (!open || pending) return
    const { inlineTimer, keepsFocus } = window.MEButtons
    return inlineTimer(group.current, () => close(keepsFocus(root.current)))
  }, [open, pending])
  return (
    <span
      ref={root}
      className={`me-inline-confirm${open ? ' open' : ''}`}
      data-me-react=""
    >
      <Button
        {...props}
        ref={trigger}
        className={`${
          props.className || 'me-btn me-btn-secondary'
        } me-inline-confirm-trigger`}
        onClick={() => setOpen(true)}
      >
        {children}
      </Button>
      <span
        ref={group}
        className="me-inline-confirm-controls"
        role="group"
        aria-label={props['data-inline-confirm']}
        onKeyDown={event => {
          if (event.key === 'Escape' && !pending) {
            event.preventDefault()
            event.stopPropagation()
            close()
          }
        }}
      >
        <span className="me-inline-confirm-q">
          {props['data-inline-confirm']}
        </span>
        <Button
          aria-disabled={blocked || undefined}
          className="me-btn me-btn-danger me-btn-xs"
          data-load="none"
          data-busy-label={props['data-busy-label']}
          data-success-label={props['data-inline-done']}
          data-failure-label={props['data-failure-label']}
          data-failure-text={props['data-failure-text']}
          onAction={() => {
            // The watch ends itself once the trigger leaves the page, so an
            // unmount needs no cleanup.
            endWatch.current = window.MEButtons.watchRemoval(
              root.current,
              trigger.current,
              containers.current
            )
            setPending(true)
            return onConfirm()
          }}
          onFailure={error => {
            endWatch.current()
            setPending(false)
            props.onFailure?.(error)
          }}
          onSuccess={() => {
            setPending(false)
            close()
          }}
        >
          {props['data-inline-verb']}
        </Button>
        <Button
          ref={cancel}
          className="me-btn me-btn-ghost me-btn-xs"
          aria-disabled={pending || blocked || undefined}
          onClick={() => close()}
        >
          {props['data-inline-cancel']}
        </Button>
      </span>
    </span>
  )
}

InlineConfirm.propTypes = {
  children: PropTypes.node.isRequired,
  // Runs the destructive request and returns its promise.
  onConfirm: PropTypes.func.isRequired,
  onFailure: PropTypes.func,
  className: PropTypes.string,
  'data-inline-confirm': PropTypes.string.isRequired,
  'data-inline-verb': PropTypes.string.isRequired,
  'data-inline-cancel': PropTypes.string.isRequired,
  'data-inline-done': PropTypes.string.isRequired,
  'data-busy-label': PropTypes.string.isRequired,
  'data-failure-label': PropTypes.string.isRequired,
  'data-failure-text': PropTypes.string.isRequired,
}
